import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { SITE } from '../data/content';

const links = [
  { to: '/', id: 'home', label: 'Inicio' },
  { to: '/menu', id: 'menu', label: 'Menú' },
  { to: '/live-music', id: 'live-music', label: 'Música en Vivo' },
  { to: '/private-events', id: 'private-events', label: 'Eventos Privados' },
  { to: '/contact', id: 'contact', label: 'Contacto' },
];

const Wordmark = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="nav-logo" className="flex items-center">
    <img src="/cantaritos-logo.png" alt="Cantaritos Dallas" className="h-12 sm:h-14 w-auto" />
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-brand-night/85 backdrop-blur-xl border-b border-brand-line ${
        scrolled ? 'shadow-lg shadow-black/40' : ''
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-12 h-20 flex items-center justify-between">
        <Wordmark />

        <ul className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `font-body text-sm uppercase tracking-widest transition-colors hover:text-brand-gold ${
                    isActive ? 'text-brand-gold' : 'text-brand-cream'
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <a
          href={`tel:${SITE.phone.replace(/[^0-9]/g, '')}`}
          data-testid="nav-phone-cta"
          className="hidden lg:inline-flex items-center bg-brand-gold text-brand-night px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-brand-citrus transition-colors"
        >
          {SITE.phone}
        </a>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-brand-cream p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-brand-night border-b border-brand-line"
          >
            <ul className="px-6 py-6 flex flex-col gap-2">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block font-display font-bold text-2xl py-2 ${
                        isActive ? 'text-brand-gold' : 'text-brand-cream'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <a
                href={`tel:${SITE.phone.replace(/[^0-9]/g, '')}`}
                className="mt-3 inline-flex items-center justify-center bg-brand-gold text-brand-night px-5 py-3 text-sm font-semibold tracking-wide"
              >
                Llamar {SITE.phone}
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
